import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useTranslation } from "react-i18next";
import {
  FaHome,
  FaEnvelope,
  FaSignOutAlt,
  FaCommentDots,
  FaDollarSign,
  FaUserCircle,
  FaUsers,
  FaUser,
  FaChevronDown,
  FaEdit,
  FaList,
} from "react-icons/fa";
import { getUserEmail, getUserId } from "../utils/userUtils";
import { listUserCharacters } from "../utils/characterUtils";
import LanguageSelector from "./LanguageSelector";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const Navbar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, signOut } = useAuthenticator((context) => [
    context.user,
    context.signOut,
  ]);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [characterDropdownOpen, setCharacterDropdownOpen] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [notification, setNotification] = useState(null);
  const [userCharacters, setUserCharacters] = useState([]);
  const dropdownRef = useRef(null);
  const characterDropdownRef = useRef(null);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const toggleCharacterDropdown = () => {
    setCharacterDropdownOpen(!characterDropdownOpen);
  };

  // Fetch user's characters
  const fetchUserCharacters = async () => {
    if (!user) {
      setUserCharacters([]);
      return;
    }

    try {
      const userId = getUserId(user);
      if (userId) {
        const characters = await listUserCharacters(userId);
        setUserCharacters(characters);
      }
    } catch (error) {
      console.error("Error fetching user characters:", error);
      setUserCharacters([]);
    }
  };

  // Helper function to show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Check if user has an active subscription
  const checkSubscription = async () => {
    if (!user) {
      setIsSubscribed(false);
      setCheckingSubscription(false);
      return;
    }

    try {
      const userId = getUserId(user);
      if (!userId) {
        setIsSubscribed(false);
        setCheckingSubscription(false);
        return;
      }

      const response = await axios.get(
        `https://d4gscm5o97.execute-api.us-east-1.amazonaws.com/prod/subscriptions/${userId}`
      );

      const subscription = response.data;
      setIsSubscribed(subscription.subscription_status === "active");
    } catch (error) {
      console.error("Error checking subscription:", error);
      setIsSubscribed(false);
    } finally {
      setCheckingSubscription(false);
    }
  };

  // Handle create character click
  const handleCreateCharacterClick = () => {
    if (!isSubscribed && !checkingSubscription) {
      showNotification("Please subscribe to create custom characters", "error");
      return;
    }
    navigate("/create-character");
  };

  // Check subscription and fetch characters when user changes
  useEffect(() => {
    checkSubscription();
    fetchUserCharacters();
  }, [user]);

  // Handle clicks outside the dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (characterDropdownRef.current && !characterDropdownRef.current.contains(event.target)) {
        setCharacterDropdownOpen(false);
      }
    };

    // Add event listener when any dropdown is open
    if (dropdownOpen || characterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, characterDropdownOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 bg-black/90 backdrop-blur-xl border-b border-white/10 p-6 z-40">
      <div className="container mx-auto flex justify-between items-center pl-16 sm:pl-4">
        <Link to="/" className="flex items-center">
          <FaHome size={20} className="text-gray-400 sm:hidden hover:text-white transition-colors duration-300" />
          <h1 className="text-2xl font-extralight text-white hidden sm:block tracking-widest">
            <span className="bg-gradient-to-r from-white via-purple-200 to-pink-200 text-transparent bg-clip-text">
              Sapiens Lab
            </span>
          </h1>
        </Link>
        <nav className="relative flex items-center space-x-4">
          <LanguageSelector />
          <Link
            to="/character-board"
            className="text-lg font-semibold text-white mx-2 hover:text-gray-400 transition duration-300"
          >
            <FaUsers size={24} className="sm:hidden" />
            <span className="hidden sm:block">{t('nav.discover')}</span>
          </Link>
          <Link
            to="/contact"
            className="text-lg font-semibold text-white mx-2 hover:text-gray-400 transition duration-300"
          >
            <FaEnvelope size={24} className="sm:hidden" />
            <span className="hidden sm:block">{t('nav.contact')}</span>
          </Link>
          {!user ? (
            <Link
              to="/controlpanel"
              className="text-lg font-semibold text-white mx-2 hover:text-gray-400 transition duration-300"
            >
              <FaSignOutAlt size={24} className="sm:hidden" />
              <span className="hidden sm:block">{t('nav.login')}</span>
            </Link>
          ) : (
            <>
              <Link
                to="/controlpanel"
                className="text-lg font-semibold text-white mx-2 hover:text-gray-400 transition duration-300"
              >
                <FaCommentDots size={24} className="sm:hidden" />
                <span className="hidden sm:block">{t('nav.controlPanel')}</span>
              </Link>

              <div className="relative" ref={characterDropdownRef}>
                <button
                  onClick={toggleCharacterDropdown}
                  disabled={!isSubscribed || checkingSubscription}
                  className={`text-lg font-semibold mx-2 transition duration-300 flex items-center ${
                    isSubscribed && !checkingSubscription
                      ? 'text-white hover:text-gray-400'
                      : 'text-gray-500 cursor-not-allowed'
                  }`}
                  title={!isSubscribed && !checkingSubscription ? 'Subscribe to manage characters' : 'Character Management'}
                >
                  <FaUser size={24} className="sm:hidden" />
                  <span className="hidden sm:flex items-center gap-1">
                    {checkingSubscription ? 'Loading...' : (
                      <>
                        Characters
                        <FaChevronDown size={12} className={`transition-transform duration-200 ${characterDropdownOpen ? 'rotate-180' : ''}`} />
                      </>
                    )}
                  </span>
                </button>

                {/* Character Management Dropdown */}
                {characterDropdownOpen && isSubscribed && (
                  <div className="absolute right-0 mt-2 w-64 bg-gray-800 border border-gray-700 rounded-xl shadow-xl z-20 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-700">
                      <p className="text-sm text-gray-400">Character Management</p>
                    </div>
                    
                    <button
                      onClick={() => {
                        navigate("/create-character");
                        setCharacterDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 text-white hover:bg-gray-700 flex items-center transition-colors"
                    >
                      <FaUser className="mr-3" />
                      <span>Create New Character</span>
                    </button>

                    {userCharacters.length > 0 && (
                      <>
                        <div className="border-t border-gray-600 my-1"></div>
                        <div className="px-4 py-2">
                          <p className="text-xs text-gray-400 mb-2">Your Characters ({userCharacters.length})</p>
                          <div className="max-h-48 overflow-y-auto">
                            {userCharacters.map((character) => (
                              <button
                                key={character.character_id}
                                onClick={() => {
                                  navigate(`/edit-character/${character.character_id}`);
                                  setCharacterDropdownOpen(false);
                                }}
                                className="w-full text-left px-3 py-2 text-white hover:bg-gray-700 flex items-center transition-colors rounded mb-1"
                              >
                                <FaEdit className="mr-3 text-blue-400" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{character.name}</p>
                                  <p className="text-xs text-gray-400 truncate">
                                    {character.is_public ? 'Public' : 'Private'}
                                  </p>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}

                    <div className="border-t border-gray-600 my-1"></div>
                    <Link
                      to="/character-management"
                      onClick={() => setCharacterDropdownOpen(false)}
                      className="w-full text-left px-4 py-3 text-white hover:bg-gray-700 flex items-center transition-colors"
                    >
                      <FaList className="mr-3" />
                      <span>Manage All Characters</span>
                    </Link>
                  </div>
                )}
              </div>

              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={toggleDropdown}
                  className="text-lg font-semibold text-white mx-2 hover:text-gray-400 transition duration-300 flex items-center"
                >
                  <FaUserCircle size={24} className="mr-2" />
                  <span className="hidden sm:block">
                    {getUserEmail(user) || "User"}
                  </span>
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-gray-800 border border-gray-700 rounded-xl shadow-xl z-20 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-700">
                      <p className="text-sm text-gray-400">{t('nav.signedInAs')}</p>
                      <p className="text-sm font-medium text-white truncate">
                        {getUserEmail(user) || "User"}
                      </p>
                    </div>
                    <Link
                      to="/subscription-management"
                      onClick={() => setDropdownOpen(false)}
                      className="w-full text-left px-4 py-3 text-white hover:bg-gray-700 flex items-center transition-colors"
                    >
                      <FaDollarSign className="mr-3" />
                      <span>{t('nav.subscription')}</span>
                    </Link>
                    <button
                      onClick={signOut}
                      className="w-full text-left px-4 py-3 text-white hover:bg-gray-700 flex items-center transition-colors"
                    >
                      <FaSignOutAlt className="mr-3" />
                      <span>{t('nav.signOut')}</span>
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </nav>
      </div>


      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 max-w-sm p-4 rounded-lg shadow-lg transform transition-all duration-300 ${
          notification.type === 'success' 
            ? 'bg-green-600 text-white border border-green-500' 
            : 'bg-red-600 text-white border border-red-500'
        }`}>
          <div className="flex items-start">
            <div className="flex-shrink-0 mr-3">
              {notification.type === 'success' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="flex-shrink-0 ml-3 text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
